import {
  CbhIcon,
  Pill,
  ScrollingNumberCounter,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { FullScreenDialog } from "@src/appV2/lib";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { useEffect, useState } from "react";

import { Button } from "../../../components/Button";
import { ClipboardScoreActions } from "../../utils/constants";

const SCORE_ANIMATION_DURATION_MS = 1500;

interface RestrictedNudgeBottomSheetContentProps {
  modalState: UseModalState;
  currentScore: number;
  scoreChange: number;
  action:
    | ClipboardScoreActions.NO_CALL_NO_SHOW
    | ClipboardScoreActions.WORKER_LEFT_EARLY_WITHOUT_PERMISSION;
  onContinue: () => void;
}

export function RestrictedNudgeBottomSheetContent(props: RestrictedNudgeBottomSheetContentProps) {
  const { modalState, currentScore, scoreChange, action, onContinue } = props;

  const [startAnimation, setStartAnimation] = useState(false);

  const startScore = currentScore - scoreChange;
  const endScore = currentScore;

  useEffect(() => {
    if (modalState.modalIsOpen) {
      setStartAnimation(false);

      // Start animation after 1 second delay
      const animationTimer = setTimeout(() => {
        setStartAnimation(true);
      }, 1000);

      return () => {
        clearTimeout(animationTimer);
      };
    }

    return undefined;
  }, [modalState.modalIsOpen]);

  const getMessages = (): { title: string; subtitle: string } => {
    return action === ClipboardScoreActions.NO_CALL_NO_SHOW
      ? {
          title: "You were marked as a no show",
          subtitle:
            "Showing up builds trust with workplaces and ensures your team isn't left short.",
        }
      : {
          title: "You were reported leaving the shift early without permission",
          subtitle:
            "Arriving to a shift while sick and being asked to leave as a result can also lead to this penalty.",
        };
  };

  const { title, subtitle } = getMessages();

  return (
    <FullScreenDialog
      modalState={modalState}
      variant="tertiary"
      PaperProps={{
        sx: {
          background:
            // eslint-disable-next-line cbh-rules/no-inline-gradient -- TODO: migrate to theme palette gradient
            "linear-gradient(180deg, rgba(251, 242, 232, 1) 0%, rgba(247, 208, 208, 1) 100%)",
        },
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack
          spacing={10}
          sx={{
            paddingTop: 10,
            paddingX: 5,
            textAlign: "center",
          }}
        >
          <Text variant="h3" textAlign="center">
            {title}
          </Text>

          <Stack spacing={6} alignItems="center">
            <Text
              variant="body2"
              textAlign="center"
              sx={{
                paddingX: 6,
                color: (theme) => theme.palette.grey[600],
              }}
            >
              Your Clipboard Score
            </Text>
            <Box
              sx={{
                transform: "scale(2.2)",
                transformOrigin: "center",
                paddingY: 8,
              }}
            >
              <ScrollingNumberCounter
                startValue={startScore}
                endValue={startAnimation ? endScore : startScore}
                duration={SCORE_ANIMATION_DURATION_MS}
                variant="h1"
                color="rgba(0, 67, 69, 1)"
              />
            </Box>

            <Text
              variant="body1"
              textAlign="center"
              sx={{
                paddingX: 6,
                color: (theme) => theme.palette.text?.secondary ?? "",
              }}
            >
              {subtitle}
            </Text>
          </Stack>

          <Stack spacing={5} alignItems="center">
            <Pill
              label={
                <Stack direction="row" gap={3} alignItems="center">
                  <CbhIcon type="diamond-colored" size="medium" />
                  <Text
                    semibold
                    variant="body2"
                    sx={{
                      color: (theme) => theme.palette.error?.main ?? "",
                    }}
                  >
                    Restricted
                  </Text>
                </Stack>
              }
              sx={{
                backgroundColor: (theme) => theme.palette.background?.tertiary ?? "",
              }}
            />
          </Stack>
        </Stack>
      </Box>
      <DialogFooter orientation="horizontal">
        <Button
          fullWidth
          variant="contained"
          size="large"
          data-testid="attendance-score-continue-button"
          onClick={onContinue}
        >
          Continue
        </Button>
      </DialogFooter>
    </FullScreenDialog>
  );
}
